import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { PauseCircle, Play, Clock, Phone, StickyNote } from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const Page = styled.div`max-width: 900px; margin: 0 auto;`;
const Card = styled.div`background: #fff; border-radius: 12px; padding: 1.5rem; box-shadow: 0 1px 3px rgba(0,0,0,0.1); margin-bottom: 1.25rem;`;
const Title = styled.h2`font-size: 1.35rem; font-weight: 700; color: #1e293b; margin: 0 0 0.25rem; display: flex; align-items: center; gap: 0.5rem;`;
const Sub = styled.p`color: #64748b; font-size: 0.9rem; margin: 0;`;
const Item = styled.div`border: 1px solid #e5e7eb; border-radius: 10px; padding: 1rem 1.25rem; margin-bottom: 0.85rem; display: flex; justify-content: space-between; align-items: center; gap: 1rem; flex-wrap: wrap;`;
const Name = styled.div`font-weight: 700; color: #1e293b; font-size: 1rem;`;
const Meta = styled.div`display: flex; gap: 1.1rem; flex-wrap: wrap; color: #64748b; font-size: 0.82rem; margin-top: 0.35rem;`;
const MetaItem = styled.span`display: inline-flex; align-items: center; gap: 0.3rem;`;
const Pill = styled.span`padding: 0.15rem 0.55rem; border-radius: 999px; font-size: 0.72rem; font-weight: 700; background: #eff6ff; color: #1d4ed8; text-transform: capitalize;`;
const Expiry = styled.span`font-weight: 700; color: ${p => p.soon ? '#b45309' : '#059669'};`;
const ResumeBtn = styled.button`padding: 0.6rem 1.1rem; background: linear-gradient(135deg,#059669,#047857); color: #fff; border: none; border-radius: 8px; font-weight: 700; cursor: pointer; display: inline-flex; align-items: center; gap: 0.45rem; &:disabled{opacity:.6;cursor:not-allowed;}`;
const Empty = styled.div`text-align: center; color: #94a3b8; padding: 2.5rem; font-style: italic;`;

const STEP_LABEL = { prescription: 'Prescription', frame: 'Frame selection', measurement: 'Measurement', payment: 'Payment' };

const fmtRemaining = (expiresAt) => {
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (ms <= 0) return { text: 'expiring', soon: true };
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return { text: h > 0 ? `expires in ${h}h ${m}m` : `expires in ${m}m`, soon: ms < 3600000 };
};

const StandbyPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resumingId, setResumingId] = useState(null);

  const load = async () => {
    const userData = localStorage.getItem('user');
    if (!userData) { setLoading(false); return; }
    try {
      const user = JSON.parse(userData);
      const userId = user.id || user._id;
      const token = localStorage.getItem('token');
      const res = await axios.get(`/api/orders/incomplete/${userId}?standby=true`, {
        headers: { Authorization: `Bearer ${token}` }, params: { _: Date.now() },
      });
      setItems(res.data?.incompleteOrders || []);
    } catch (e) {
      console.error('Error loading standby list:', e);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const resume = async (item) => {
    const id = item.prescription?._id || item.order?._id;
    setResumingId(id);
    try {
      const visitor = item.visitor || {};
      const prescription = item.prescription || null;
      const visitorData = {
        gender: visitor.gender || '', ageGroup: visitor.age_group || visitor.ageGroup || '',
        firstName: visitor.first_name || visitor.firstName || '', lastName: visitor.last_name || visitor.lastName || '',
        phone: visitor.phone || prescription?.phoneNumber || '', visitDate: visitor.created_at || new Date().toISOString(),
      };
      sessionStorage.setItem('visitorData', JSON.stringify(visitorData));
      if (visitor._id || visitor.id) sessionStorage.setItem('visitorId', visitor._id || visitor.id);
      if (prescription) {
        sessionStorage.setItem('existingPrescription', JSON.stringify(prescription));
        if (prescription._id) sessionStorage.setItem('prescriptionId', prescription._id);
      }
      // Un-park it so it leaves the standby list.
      const token = localStorage.getItem('token');
      await axios.post('/api/orders/standby/clear',
        { prescriptionId: prescription?._id, orderId: item.order?._id },
        { headers: { Authorization: `Bearer ${token}` } });
      navigate('/prescription');
    } catch (e) {
      console.error('Error resuming standby order:', e);
      setResumingId(null);
      alert('Could not resume this order. Please try again.');
    }
  };

  return (
    <Page>
      <Card>
        <Title><PauseCircle size={22} color="#d97706" /> {t('standbyPage.standby')}</Title>
        <Sub>{t('standbyPage.ordersYouParkedResumeOne')}</Sub>
      </Card>

      {loading ? (
        <Card><Empty>{t('standbyPage.loading')}</Empty></Card>
      ) : items.length === 0 ? (
        <Card><Empty>{t('standbyPage.noOrdersOnStandby')}</Empty></Card>
      ) : items.map((item, i) => {
        const name = item.prescription?.patientName
          || `${item.visitor?.first_name || item.visitor?.firstName || ''} ${item.visitor?.last_name || item.visitor?.lastName || ''}`.trim()
          || 'Visitor';
        const sb = item.standby || {};
        const rem = sb.expiresAt ? fmtRemaining(sb.expiresAt) : { text: '', soon: false };
        return (
          <Item key={item.prescription?._id || item.order?._id || i}>
            <div>
              <Name>{name}</Name>
              <Meta>
                {sb.step && <MetaItem><Pill>{STEP_LABEL[sb.step] || sb.step}</Pill></MetaItem>}
                {sb.expiresAt && <MetaItem><Clock size={14} /> <Expiry soon={rem.soon}>{rem.text}</Expiry></MetaItem>}
                {(item.prescription?.phoneNumber || item.visitor?.phone) && <MetaItem><Phone size={14} /> {item.prescription?.phoneNumber || item.visitor?.phone}</MetaItem>}
                {sb.notes && <MetaItem><StickyNote size={14} /> {sb.notes}</MetaItem>}
              </Meta>
            </div>
            <ResumeBtn onClick={() => resume(item)} disabled={!!resumingId}>
              <Play size={15} /> {resumingId === (item.prescription?._id || item.order?._id) ? t('standbyPage.resuming') : t('standbyPage.resume')}
            </ResumeBtn>
          </Item>
        );
      })}
    </Page>
  );
};

export default StandbyPage;
